import { useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import Reveal from "@/components/base/Reveal";
import usePageMeta from "@/hooks/usePageMeta";
import { articles, categories, siteInfo } from "@/mocks/news";

export default function CategoryPage() {
  const { slug = "all" } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [keyword, setKeyword] = useState(searchParams.get("q") || "");

  const category = categories.find((item) => item.slug === slug);
  const isAll = slug === "all";
  const exists = isAll || Boolean(category);

  const baseList = useMemo(
    () => (isAll ? articles : articles.filter((item) => item.categorySlug === slug)),
    [isAll, slug],
  );

  const activeKeyword = (searchParams.get("q") || "").trim().toLowerCase();

  const list = useMemo(() => {
    if (!activeKeyword) return baseList;
    return baseList.filter(
      (item) =>
        item.title.toLowerCase().includes(activeKeyword) ||
        item.excerpt.toLowerCase().includes(activeKeyword) ||
        item.tags.join(" ").toLowerCase().includes(activeKeyword),
    );
  }, [baseList, activeKeyword]);

  const pageTitle = isAll
    ? "全部速递 - 独家黑料快报与最新条目｜料闻社"
    : `${category?.name} - 独家黑料速递栏目｜料闻社`;
  const pageDesc = isAll
    ? "料闻社全部速递条目汇总，按时间收录独家黑料快报、人物动态与事件时间线梳理，支持关键词检索。"
    : `料闻社「${category?.name}」栏目：${category?.intro}`;

  const sortByDate = useMemo(
    () => [...list].sort((a, b) => (a.date < b.date ? 1 : -1)),
    [list],
  );

  const jsonLd = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: isAll ? "全部速递" : `料闻社 · ${category?.name}`,
        description: pageDesc,
        inLanguage: "zh-CN",
        url: `https://sl858.cn/category/${slug}`,
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://sl858.cn/" },
          {
            "@type": "ListItem",
            position: 2,
            name: isAll ? "全部速递" : category?.name,
            item: `https://sl858.cn/category/${slug}`,
          },
        ],
      },
    ],
    [isAll, slug, category, pageDesc],
  );

  usePageMeta({
    title: pageTitle,
    description: pageDesc,
    keywords: `${isAll ? "独家黑料,独家黑料速递,独家黑料快报" : `${category?.name},独家黑料,${category?.name}速递`},事件时间线梳理`,
    path: `/category/${slug}`,
    jsonLd,
  });

  const applySearch = (value: string) => {
    setKeyword(value);
    if (value.trim()) {
      setSearchParams({ q: value.trim() });
    } else {
      setSearchParams({});
    }
  };

  if (!exists) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <SiteHeader />
        <main className="flex flex-1 items-center justify-center px-4 py-24">
          <div className="flex flex-col items-center gap-4 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100">
              <i className="ri-file-unknow-line text-2xl text-foreground-500" />
            </span>
            <h1 className="font-heading text-xl font-bold text-foreground-950">栏目不存在</h1>
            <p className="max-w-md text-sm text-foreground-600">
              你访问的栏目可能已经调整。可以先回到全部速递页，或用下方入口进入其他栏目。
            </p>
            <Link
              to="/category/all"
              className="inline-flex h-11 cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              进入全部速递
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />

      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1400px] px-4 py-8 md:px-8 md:py-11">
          <Breadcrumb items={[{ label: isAll ? "全部速递" : category?.name || "" }]} />
          <div className="mt-4 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="w-full">
              <h1 className="font-heading text-2xl font-black text-foreground-950 md:text-4xl">
                {isAll ? "全部速递" : category?.name}
              </h1>
              <p className="mt-2.5 max-w-2xl text-sm leading-relaxed text-foreground-600">
                {isAll
                  ? "按时间收录全站独家黑料快报与人物动态条目，支持关键词检索与栏目跳转。"
                  : category?.intro}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-3 text-xs text-foreground-500">
              <span className="rounded-md border border-background-300 bg-background-50 px-3 py-1.5">
                共 {baseList.length} 组条目
              </span>
              <span className="rounded-md border border-background-300 bg-background-50 px-3 py-1.5">
                {siteInfo.updateNote}
              </span>
            </div>
          </div>
        </div>
      </section>

      <main className="mx-auto w-full max-w-[1400px] flex-1 px-4 py-10 md:px-8 md:py-14">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_300px] lg:gap-12">
          <div className="w-full">
            <div className="flex items-center rounded-md border border-background-300 bg-background-100 pl-3">
              <span className="flex h-11 w-5 items-center justify-center">
                <i className="ri-search-line text-sm text-foreground-500" />
              </span>
              <input
                type="search"
                value={keyword}
                onChange={(e) => applySearch(e.target.value)}
                placeholder="在本栏目内搜索标题或标签"
                aria-label="在栏目内搜索"
                className="h-11 flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
              />
              {keyword && (
                <button
                  type="button"
                  onClick={() => applySearch("")}
                  aria-label="清空搜索"
                  className="mr-1 flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-200"
                >
                  <i className="ri-close-line" />
                </button>
              )}
            </div>

            {activeKeyword && (
              <p className="mt-3 text-xs text-foreground-500">
                关键词「{searchParams.get("q")}」共匹配 {sortByDate.length} 条结果
              </p>
            )}

            {sortByDate.length > 0 ? (
              <div className="mt-6 space-y-5">
                {sortByDate.map((article, index) => (
                  <Reveal key={article.id} delay={Math.min(index, 6) * 50}>
                    <ArticleCard article={article} variant="wide" />
                  </Reveal>
                ))}
              </div>
            ) : (
              <div className="mt-8 flex flex-col items-center gap-3 rounded-lg border border-dashed border-background-300 bg-background-100/60 py-16">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-200">
                  <i className="ri-search-eye-line text-xl text-foreground-500" />
                </span>
                <p className="text-sm text-foreground-700">这个栏目下暂时没有匹配的条目。</p>
                <button
                  type="button"
                  onClick={() => applySearch("")}
                  className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  清空关键词
                </button>
              </div>
            )}
          </div>

          <aside className="w-full space-y-6">
            <div className="rounded-lg border border-background-200 bg-background-100/70 p-4">
              <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <span className="flex h-5 w-5 items-center justify-center">
                  <i className="ri-folder-open-line text-primary-600" />
                </span>
                切换栏目
              </h2>
              <ul className="mt-3 space-y-1">
                <li>
                  <Link
                    to="/category/all"
                    className={`flex items-center justify-between rounded-md px-3 py-2 text-sm transition-colors ${
                      isAll
                        ? "bg-primary-100 text-primary-700"
                        : "text-foreground-700 hover:bg-background-200"
                    }`}
                  >
                    全部速递
                    <span className="text-xs text-foreground-500">{articles.length}</span>
                  </Link>
                </li>
                {categories.map((cat) => {
                  const count = articles.filter((item) => item.categorySlug === cat.slug).length;
                  return (
                    <li key={cat.slug}>
                      <Link
                        to={`/category/${cat.slug}`}
                        className={`flex items-center justify-between rounded-md px-3 py-2 text-sm transition-colors ${
                          slug === cat.slug
                            ? "bg-primary-100 text-primary-700"
                            : "text-foreground-700 hover:bg-background-200"
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <i className={cat.icon} />
                          {cat.name}
                        </span>
                        <span className="text-xs text-foreground-500">{count}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-100/70 p-4">
              <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <span className="flex h-5 w-5 items-center justify-center">
                  <i className="ri-fire-line text-primary-600" />
                </span>
                本栏目关注度高
              </h2>
              <ul className="mt-2">
                {[...baseList]
                  .sort((a, b) => b.views - a.views)
                  .slice(0, 5)
                  .map((item, index) => (
                    <li key={item.id}>
                      <ArticleCard article={item} variant="compact" index={index} />
                    </li>
                  ))}
              </ul>
            </div>

            <div className="rounded-lg border border-accent-200 bg-accent-50 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">
                订阅每日速递
              </h2>
              <p className="mt-2 text-xs leading-relaxed text-foreground-600">
                早八点推送当日清单，每条标注依据类型，不把线索当结论。
              </p>
              <Link
                to="/#subscribe"
                className="mt-4 inline-flex h-10 w-full cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-mail-add-line" />
                前往订阅
              </Link>
            </div>
          </aside>
        </div>
      </main>

      <SiteFooter />
      <BackToTop />
    </div>
  );
}